import type { AnnotationType } from '@/state/slices/measurementSession';

// Cornerstone annotation → backend payload helpers (AP-676). Backend rules:
// POINT = 1 point, LINE = 2, ANGLE = 3, FREEHAND ≥ 2; each point is [x, y, z].

export type RawPoint =
  | { x?: number; y?: number; z?: number }
  | number[]
  | null
  | undefined;

export interface AnnotationGeometrySource {
  contour?: { polyline?: RawPoint[] };
  handles?: { points?: RawPoint[] };
}

function toWorldPoint(p: RawPoint): [number, number, number] {
  if (p == null) return [0, 0, 0];
  return Array.isArray(p)
    ? [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0]
    : [p.x ?? 0, p.y ?? 0, p.z ?? 0];
}

/** True when the drawing tool stored its geometry as a contour outline
 *  (PlanarFreehandROI keeps only sparse handles). */
export function hasContourGeometry(
  data: AnnotationGeometrySource | null | undefined
): boolean {
  return (data?.contour?.polyline?.length ?? 0) > 0;
}

/** Points for the persisted annotation, from whichever geometry store the
 *  drawing tool used: contour polyline when present, handle points otherwise. */
export function buildAnnotationPoints(
  data: AnnotationGeometrySource | null | undefined
): [number, number, number][] {
  const source = hasContourGeometry(data)
    ? data!.contour!.polyline!
    : (data?.handles?.points ?? []);
  return source.map(toWorldPoint);
}

export interface AnnotationPayload {
  type: AnnotationType;
  dicomUuid: string | undefined;
  frameNumber: number;
  points: [number, number, number][];
}

const POINT_COUNT_RULES: Record<AnnotationType, (n: number) => boolean> = {
  POINT: (n) => n === 1,
  LINE: (n) => n === 2,
  ANGLE: (n) => n === 3,
  FREEHAND: (n) => n >= 2,
};

/** Mirrors the backend AnnotationSerializer rules so invalid payloads are
 *  caught at capture time instead of 400-blocking the atomic report save. */
export function isBackendValidAnnotation(
  meta: AnnotationPayload | null | undefined
): meta is AnnotationPayload & { dicomUuid: string } {
  if (!meta?.dicomUuid || !Number.isFinite(meta.frameNumber)) return false;
  const rule = POINT_COUNT_RULES[meta.type];
  if (!rule || !Array.isArray(meta.points) || !rule(meta.points.length)) {
    return false;
  }
  return meta.points.every(
    (p) => Array.isArray(p) && p.length === 3 && p.every(Number.isFinite)
  );
}
